import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { updateQueueMember } from 'flow/actions/pbx-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { selectExtensionActiveProfileKey } from 'flow/selectors/ExtensionSelectors';
import { selectAllQueueMembers } from 'flow/selectors/queue-selectors';
import { ExtensionKey, ProfileKey, QueueKey } from 'flow/types/EntityKeys';
import { QueueMember } from 'flow/types/Queue';
import { QueueLoginList } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { fromJS } from 'immutable';
import React from 'react';
import { useParams } from 'react-router-dom';

const Queues = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { profiles, getProfile } = useProfiles(extensionKey);

    const extensionActiveProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, extensionKey),
    );

    const allQueueMembers = useAppSelector(selectAllQueueMembers);

    const queueLoginList = getProfile(profileKey).get('queueLoginList');

    const queuesLoggedIn = queueLoginList
        .filter((queueLoginList) => queueLoginList.get('loggedIn'))
        .map((queueLoginList) => queueLoginList.get('queueEntityKey'));

    const handleChange = async (key: QueueKey, value: boolean) => {
        const updatedProfileDTO = fromJS({
            key: profileKey,
            queueLoginList: updateOrCreateLoginForQueue(key, value),
        });

        dispatch(updateProfile(extensionKey, updatedProfileDTO));

        if (extensionActiveProfileKey === profileKey) {
            const memberDTO = allQueueMembers
                .get(key)
                ?.find(
                    (member: TypedMap<QueueMember>) =>
                        member.get('extensionKey') === extensionKey,
                );
            if (memberDTO) {
                const updatedMemberDTO = memberDTO.set('loggedIn', value);
                dispatch(updateQueueMember(key, updatedMemberDTO));
            }
        }
    };

    const updateOrCreateLoginForQueue = (
        queueEntityKey: QueueKey,
        loggedIn: boolean,
    ) => {
        const indexOfQueueLoginToUpdate = queueLoginList.findIndex(
            (queue) => queue.get('queueEntityKey') === queueEntityKey,
        );

        if (indexOfQueueLoginToUpdate === -1) {
            // The list of queueLogins had no entry for this queue. Let's create it.
            return queueLoginList.push({
                loggedIn,
                queueEntityKey,
            });
        }

        // The queueLogins had an entry for this queue. We'll update it.
        return queueLoginList.setIn(
            [indexOfQueueLoginToUpdate, 'loggedIn'],
            loggedIn,
        );
    };

    return (
        <>
            <SettingSection className="text-sm/4">
                {i18next.t('pbx.queues')}
            </SettingSection>

            {profiles
                .first()
                .get('queueLoginList')
                .sort((a, b) =>
                    a
                        .get('queueDescription')
                        .localeCompare(b.get('queueDescription')),
                )
                .map((queue: TypedMap<QueueLoginList>) => (
                    <SettingRow
                        key={queue.get('queueEntityKey')}
                        cta={
                            <Toggle
                                value={queuesLoggedIn.includes(
                                    queue.get('queueEntityKey'),
                                )}
                                onChange={(value) =>
                                    handleChange(
                                        queue.get('queueEntityKey'),
                                        value,
                                    )
                                }
                                text={queue.get('queueDescription')}
                            />
                        }
                    >
                        {queue.get('queueDescription')}
                    </SettingRow>
                ))}
        </>
    );
};

export default Queues;
